import { useEffect, useState } from "react";
import { api, can } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Check, X, RefreshCw, Archive, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  pending: "bg-amber-600/20 text-amber-400 border-amber-600/40",
  approved: "bg-emerald-600/20 text-emerald-400 border-emerald-600/40",
  rejected: "bg-rose-600/20 text-rose-400 border-rose-600/40",
};
const STATUS_LABELS = { pending: "Beklemede", approved: "Onaylandı", rejected: "Reddedildi" };

export default function Excuses() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ reason: "", start_date: "", end_date: "" });
  const [reviewOpen, setReviewOpen] = useState(null);
  const [reviewNote, setReviewNote] = useState("");
  const canReview = can(user.role, "founder", "admin", "officer");
  const canArchive = can(user.role, "founder", "admin");
  const canDelete = can(user.role, "founder");

  const load = () => api.get("/excuses").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.reason.trim()) return toast.error("Mazeret metni gerekli");
    try {
      await api.post("/excuses", form);
      toast.success("Mazeret gönderildi");
      setOpen(false); setForm({ reason:"", start_date:"", end_date:"" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gönderilemedi"); }
  };

  const review = async (status) => {
    try {
      await api.post(`/excuses/${reviewOpen.id}/review`, { status, review_note: reviewNote });
      toast.success(status === "approved" ? "Onaylandı" : "Reddedildi");
      setReviewOpen(null); setReviewNote(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
  };

  const revert = async (e) => {
    try {
      await api.put(`/excuses/${e.id}`, { status: "pending", review_note: "Karar geri alındı" });
      toast.success("Geri alındı"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Başarısız"); }
  };

  const archive = async (e) => {
    if (!window.confirm("Bu mazeret arşivlensin mi?")) return;
    try { await api.post(`/excuses/${e.id}/archive`); toast.success("Arşivlendi"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Başarısız"); }
  };

  const deleteOne = async (e) => {
    if (!window.confirm("Bu mazeret kalıcı olarak silinsin mi?")) return;
    try { await api.delete(`/excuses/${e.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Başarısız"); }
  };

  const clearArchive = async () => {
    if (!window.confirm("Arşivdeki TÜM mazeretler kalıcı olarak silinsin mi? Bu işlem geri alınamaz.")) return;
    try {
      const { data } = await api.delete("/excuses/archive/clear");
      toast.success(`${data.deleted} mazeret silindi`); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Başarısız"); }
  };

  const filtered = (status) => items.filter(i =>
    (status === "history" ? i.archived : (!i.archived && (status === "all" || i.status === status)))
  );

  const renderList = (list) => (
    <div className="space-y-3">
      {list.length === 0 && (
        <Card className="bg-zinc-950 border-zinc-900 p-10 text-center">
          <FileText className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
          <p className="text-zinc-500 text-sm">Kayıt bulunamadı.</p>
        </Card>
      )}
      {list.map(e => (
        <Card key={e.id} className="bg-zinc-950 border-zinc-900 p-5" data-testid={`excuse-${e.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className={`text-[10px] font-mono px-2 py-0.5 border ${STATUS_STYLES[e.status]}`}>{STATUS_LABELS[e.status]}</span>
                <span className="font-semibold">{e.user_name}</span>
                <span className="font-mono text-[10px] text-zinc-500">{e.hex_id}</span>
                <span className="text-[10px] font-mono text-zinc-500">// {new Date(e.created_at).toLocaleString("tr-TR")}</span>
              </div>
              <p className="text-zinc-300 whitespace-pre-wrap">{e.reason}</p>
              {(e.start_date || e.end_date) && (
                <div className="text-[11px] font-mono text-zinc-500 mt-2">{e.start_date || "?"} → {e.end_date || "?"}</div>
              )}
              {e.review_note && (
                <div className="mt-2 text-xs text-zinc-400 bg-zinc-900/50 p-2 border-l-2 border-zinc-700">
                  <span className="font-mono text-zinc-500">{e.reviewed_by_name}:</span> {e.review_note}
                </div>
              )}
            </div>
            {canReview && !e.archived && (
              <div className="flex flex-col gap-2">
                {e.status === "pending" ? (
                  <Button size="sm" onClick={()=>{setReviewOpen(e); setReviewNote("");}} data-testid={`review-excuse-${e.id}`} className="bg-rose-600 hover:bg-rose-700">İnceleme</Button>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" onClick={()=>revert(e)} data-testid={`revert-excuse-${e.id}`}><RefreshCw className="w-4 h-4 mr-1" /> Geri Al</Button>
                    {canArchive && <Button size="sm" variant="ghost" onClick={()=>archive(e)} data-testid={`archive-excuse-${e.id}`}><Archive className="w-4 h-4 mr-1" /> Arşivle</Button>}
                  </>
                )}
              </div>
            )}
            {canDelete && e.archived && (
              <button onClick={() => deleteOne(e)} data-testid={`delete-excuse-${e.id}`} title="Kalıcı olarak sil"
                className="text-zinc-600 hover:text-rose-400 transition-colors shrink-0">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-5xl">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">// LEAVE REQUESTS</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Mazeretler</h1>
          <p className="text-zinc-500 mt-2">Ekipten uzak kalacağın günleri buradan bildir.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-excuse-button" className="bg-rose-600 hover:bg-rose-700">
              <Plus className="w-4 h-4 mr-2" /> Mazeret Gönder
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800">
            <DialogHeader><DialogTitle className="font-heading">Yeni Mazeret</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label className="text-xs font-mono">SEBEP</Label>
                <Textarea rows={5} value={form.reason} onChange={e=>setForm({...form,reason:e.target.value})} data-testid="excuse-reason-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs font-mono">BAŞLANGIÇ</Label>
                  <Input type="date" value={form.start_date} onChange={e=>setForm({...form,start_date:e.target.value})} data-testid="excuse-start-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
                <div><Label className="text-xs font-mono">BİTİŞ</Label>
                  <Input type="date" value={form.end_date} onChange={e=>setForm({...form,end_date:e.target.value})} data-testid="excuse-end-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={submit} data-testid="excuse-submit-button" className="bg-rose-600 hover:bg-rose-700">Gönder</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <Tabs defaultValue="pending">
        <TabsList className="bg-zinc-950 border border-zinc-900">
          <TabsTrigger value="pending" data-testid="tab-pending">Bekleyen</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Onaylı</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Red</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">Tümü</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Arşiv</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-4">{renderList(filtered("pending"))}</TabsContent>
        <TabsContent value="approved" className="mt-4">{renderList(filtered("approved"))}</TabsContent>
        <TabsContent value="rejected" className="mt-4">{renderList(filtered("rejected"))}</TabsContent>
        <TabsContent value="all" className="mt-4">{renderList(filtered("all"))}</TabsContent>
        <TabsContent value="history" className="mt-4 space-y-3">
          {canDelete && filtered("history").length > 0 && (
            <div className="flex justify-end">
              <Button size="sm" variant="outline" onClick={clearArchive} data-testid="clear-archive-button"
                className="border-rose-800 text-rose-400 hover:bg-rose-950/30"><Trash2 className="w-4 h-4 mr-2" />Tüm Arşivi Temizle</Button>
            </div>
          )}
          {renderList(filtered("history"))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!reviewOpen} onOpenChange={v=>!v && setReviewOpen(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader><DialogTitle className="font-heading">Mazereti İncele</DialogTitle></DialogHeader>
          {reviewOpen && (
            <div className="space-y-3">
              <div className="text-sm text-zinc-400">
                <span className="font-semibold text-zinc-200">{reviewOpen.user_name}</span> · <span className="font-mono text-xs">{reviewOpen.hex_id}</span>
              </div>
              <p className="text-zinc-300 bg-zinc-900/60 p-3 border-l-2 border-rose-500/50 whitespace-pre-wrap">{reviewOpen.reason}</p>
              <div><Label className="text-xs font-mono">NOT (opsiyonel)</Label>
                <Textarea rows={3} value={reviewNote} onChange={e=>setReviewNote(e.target.value)} data-testid="review-note-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button onClick={()=>review("rejected")} data-testid="reject-excuse-button" variant="outline" className="border-rose-600/40 text-rose-400 hover:bg-rose-950/30"><X className="w-4 h-4 mr-1" /> Reddet</Button>
            <Button onClick={()=>review("approved")} data-testid="approve-excuse-button" className="bg-emerald-600 hover:bg-emerald-700"><Check className="w-4 h-4 mr-1" /> Onayla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
